<template>
  <div
    v-if="task"
    class="relative flex flex-col
            max-sm:bg-chrome
           sm:z-1 sm:w-80 sm:flex-none sm:border-l sm:border-line sm:px-4
           sm:before:pointer-events-none sm:before:absolute sm:before:inset-0 sm:before:-z-1
           sm:before:content-[''] sm:before:bg-chrome sm:before:[backdrop-filter:var(--glass-blur)]
           max-sm:pointer-events-none max-sm:fixed max-sm:top-(--mobile-header-height) max-sm:right-0
           max-sm:bottom-(--mobile-nav-height) max-sm:z-(--z-panel)
           max-sm:before:fixed max-sm:before:inset-0 max-sm:before:z-1 max-sm:before:content-['']
           max-sm:before:transition-colors max-sm:before:duration-150
           max-sm:[&.rightBarOpen]:pointer-events-auto
           max-sm:[&.rightBarOpen]:before:bg-backdrop"
    @click="handleRootClick"
  >
    <div
      ref="nestedRef"
      class="flex min-h-0 flex-1 flex-col
             max-sm:fixed max-sm:top-(--mobile-header-height) max-sm:bottom-(--mobile-nav-height) max-sm:z-2
             max-sm:-right-72 max-sm:max-sm:w-72 max-sm:px-5
             max-sm:bg-chrome max-sm:[backdrop-filter:var(--glass-blur)]
             max-sm:transition-[right] max-sm:duration-150
             max-sm:[.rightBarOpen_&]:right-0"
    >
      <div class="v-tracklify-scroll flex min-h-0 grow flex-col gap-2 overflow-y-auto pt-6">

        <div class="flex items-center gap-1.5">
          <SimpleButton
            v-if="!task.parentTaskId"
            variant="green"
            size="sm"
            class="w-8! px-0!"
            data-no-create-click-outside
            :title="t('ui.task_right_bar_add_subtask_title')"
            @click="toggleCreateSubtask"
          >
            <IconSquaresPlus16Solid class="size-4" />
          </SimpleButton>

          <div class="ml-auto flex items-center gap-1.5">
            <SimpleButton
              size="sm"
              class="w-8! px-0!"
              :title="t('task.Screenshots')"
              :pressed="!!screenshotsPage"
              @click="screenshotsPage ? emit('leaveScreenshotsPage') : emit('screenshotsPage')"
            >
              <IconCamera16Solid class="size-4" />
            </SimpleButton>

            <SimpleButton
              size="sm"
              class="w-8! px-0!"
              :red="getCurrentTtbu().liked"
              @click="taskLikedControl(task.id)"
            >
              <IconHeart16Solid class="size-4" />
            </SimpleButton>

            <Dropdown stickRight closeOnClick>
              <template #wrapper>
                <SimpleButton size="sm" class="w-8! px-0!">
                  <IconEllipsisVertical16Solid class="size-4" />
                </SimpleButton>
              </template>
              <template #items>
                <div
                  v-for="action in menuActions"
                  :key="action.label"
                  class="flex cursor-pointer items-center gap-2 rounded-chip px-3 py-2
                         whitespace-nowrap transition-colors duration-100 hover:bg-hover"
                  @click="action.run()"
                >
                  <component :is="action.icon" class="size-4 shrink-0" />
                  {{ action.label }}
                </div>
              </template>
            </Dropdown>

            <CloseButton
              v-if="!mobileMode"
              @click="emit('closeDialog'); emit('screenshotsPage')"
              :allowCloseOnEsc="imageIsNotZoomed"
            ></CloseButton>
          </div>
        </div>

        <div data-testid="share-section" class="flex items-center gap-1.5">
          <div
            class="flex h-8 min-w-0 flex-1 items-center justify-center rounded-control border
                   border-line bg-card px-2 text-label text-text-muted"
          > 
            <span class="truncate" :title="prefixName">{{ prefixName }}</span>
          </div>
          <SimpleButton
            size="sm"
            class="h-8! w-8! px-0!"
            :title="t('task.copy_to_clipboard')"
            @click="copyTaskLink()"
          >
            <IconShare16Solid class="size-4" />
          </SimpleButton>
        </div>

        <div class="grid grid-cols-2 gap-1.5">
          <PropertyTile data-testid="created-by-section" :label="t('task.Created_by')" class="min-h-23">
            <UserAvatar
              v-if="task.creatorId"
              withDayoffBadge
              :user="getUser(task.creatorId)"
            />
            <span class="flex items-center gap-1 text-label text-text-muted">
              <Time :time="task.createdAt" />
              {{ t('task.ago') }}
            </span>
          </PropertyTile>

          <PriorityTile
            data-testid="priority"
            :priority="task.priority"
            @update="updatePriority"
          />

          <MemberTile
            data-testid="executor-section"
            :label="t('task.Executor')"
            withStar
            :user="task.executorId ? getUser(task.executorId) : null"
            :items="executorSelectListItems"
            @select="updateExecutor"
          />

          <MemberTile
            data-testid="assignee-section"
            :label="t('task.ASSIGN')"
            :user="task.assigneeId ? getUser(task.assigneeId) : null"
            :items="assigneeSelectListItems"
            @select="updateAssignee"
          />

          <Dropdown data-testid="stage-section" stretch stickRight closeOnClick>
            <template #wrapper>
              <PropertyTile :label="t('task.Stage')" interactive class="min-h-17 w-full">
                <span class="w-full truncate text-body font-bold text-text">
                  {{ stageName || t('task.NoStage') }}
                </span>
              </PropertyTile>
            </template>
            <template #items>
              <AppItemsList
                class="w-40"
                :items="project.stages"
                :getLabel="stage => stage.name"
                @select="updateStage"
              />
            </template>
          </Dropdown>

          <PropertyTile data-testid="progress-section" :label="t('task.Progress')" class="min-h-17">
            <ChecklistProgress v-if="checklist.length" class="w-full" :checklist="checklist" />
            <span v-else class="text-body font-bold text-text">{{ t('task.Unset') }}</span>
          </PropertyTile>
        </div>

        <div data-testid="tags-section" class="shrink-0 overflow-hidden rounded-card border border-line bg-card">
          <div class="flex min-h-9 items-center gap-2 px-2.5 py-1">
            <span class="text-label text-text-muted">{{ t('task.Tags') }}</span>

            <TaskLabelSelect
              class="ml-auto"
              :task="task"
              :project="project"
              @set="setLabelForTask"
            >
              <template #activator>
                <SimpleButton
                  size="icon"
                  variant="ghost"
                  class="size-7! p-0!"
                  :title="t('task.change')"
                  :aria-label="t('task.change')"
                >
                  <IconPencil16Solid class="size-3.5" />
                </SimpleButton>
              </template>
            </TaskLabelSelect>
          </div>

          <div
            v-if="task.labels && task.labels.length"
            class="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto px-2.5 pb-2.5 v-tracklify-scroll"
          >
            <div
              v-for="tag in task.labels"
              :key="tag.id"
              class="tag-tint flex h-5.5 shrink min-w-5 items-center justify-center overflow-hidden
                     rounded-chip border px-2 text-label font-bold leading-none"
              :style="{ '--tag-color': `#${tag.color}` }"
            >
              <div class="truncate" :title="tag.name">{{ tag.name }}</div>
            </div>
          </div>
        </div>

        <div
          data-testid="task-times"
          class="flex flex-col gap-2 rounded-card border border-line bg-card px-3 py-2.5"
        >
          <div
            class="flex flex-col items-center gap-1"
            :class="spentPercent !== null && spentPercent > 100 ? 'text-red' : 'text-accent'"
          >
            <span class="text-label text-text-muted">{{ t('task.spent_time') }}</span>
            <SpentTime v-if="spentPercent === null" class="text-group font-bold" :seconds="spentSeconds" />
            <span v-else class="text-group font-bold">{{ spentPercent }}%</span>
            <button
              v-if="manualSeconds"
              type="button"
              class="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-unit
                     text-text-muted transition-colors duration-150 hover:text-text"
              :title="formatDuration(manualSeconds, t)"
              @click="emit('scrollToManualTime')"
            >
              <i class="tr tr-manual-time flex"></i>
              {{ manualPercent }}%
            </button>
            <div v-if="spentPercent !== null" class="h-1 w-full overflow-hidden rounded-full bg-current/25">
              <div
                class="h-full rounded-full bg-current"
                :style="{ width: `${Math.min(spentPercent, 100)}%` }"
              ></div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-1.5">
            <Dropdown data-testid="estimate-section" stretch stickRight closeOnClick ref="estimateDropdownRef">
              <template #wrapper>
                <SimpleButton data-testid="estimate-button" size="sm" class="h-9! w-full gap-1.5 px-2!">
                  <IconClock16Solid class="size-4 shrink-0" />
                  {{ task.estimate ? task.estimate.toString() : t('task.Estimate') }}
                </SimpleButton>
              </template>

              <template #items>
                <div class="flex w-44 flex-col gap-3 p-1">
                  <div class="text-label text-text-muted">
                    <div class="font-bold text-text">{{ t('task.SET_ESTIMATE') }}</div>
                    <div>{{ t('task.STORY_POINTS') }}</div>
                  </div>

                  <div class="flex flex-wrap gap-1">
                    <SimpleButton
                      v-for="est in [1, 2, 3, 5, 8, 13]"
                      :key="est"
                      size="sm"
                      class="w-8! px-0!"
                      :pressed="`${task.estimate}` === `${est}`"
                      @click="updateEstimate(est.toString())"
                    >
                      {{ est }}
                    </SimpleButton>
                  </div>

                  <SingleLineInput
                    @click.stop
                    :modelValue="task.estimate ?? ''"
                    :initialyFocused="shouldEstAutofocus"
                    type="number"
                    :placeholder="t('task.Estimate')"
                    name="estimate"
                    @keyup.enter="onEstimateEnter($event)"
                    @update:modelValue="updateEstimateDebounce($event)"
                  />
                  <SimpleButton size="sm" class="w-full" @click="clearEstimate">
                    {{ t('task.Clear') }}
                  </SimpleButton>
                </div>
              </template>
            </Dropdown>

            <SimpleButton
              size="sm"
              class="h-9! gap-1.5 px-2!"
              :title="t('task.Add_manual_time')"
              @click="emit('manualTimeDialog')"
            >
              <i class="tr tr-add_manual"></i>
              {{ t('task.Add_time') }}
            </SimpleButton>
          </div>

          <TrackerChips
            v-if="ttbus.length"
            data-testid="ttbus-holder"
            :ttbus="ttbus"
          />
        </div>
      </div>

      <div v-if="getRulesStage" class="mt-auto pt-2 pb-4">
        <RuleButtons
          :rules="getRulesStage"
          :transferUser="transferUser"
          :stages="project.stages"
          :assignedMember="task.assigneeId"
          :taskCreator="task.creatorId"
          :showMe="true"
          :currentStageId="task.stageId"
          :isCurrentUserActive="currentUserTime.tracking"
          :autoclickStartPoint="currentUserTime.startPoint"
          @secondaryClick="() => runRules('secondary')"
          @primaryClick="() => runRules('primary')"
        />
      </div>
    </div>

    <SmallPopup :show="showTransferPopup" :showClose="false" @close="closeTransferPopup">
      <div class="flex w-96 max-w-full flex-col gap-4">
        <div
          class="relative -mx-4 -mt-4 h-28 overflow-hidden rounded-t-card"
          :style="{ backgroundColor: project.bgColor }"
        >
          <img
            v-if="project.backgroundUrl"
            :src="project.backgroundUrl"
            alt=""
            class="size-full object-cover"
          >
          <div class="absolute inset-0 bg-gradient-to-t from-backdrop-strong to-transparent"></div>
          <div class="absolute inset-x-4 bottom-3">
            <div class="text-label text-white/70">{{ t('task.Change_project') }}:</div>
            <div class="truncate text-title font-bold text-white">{{ project.name }}</div>
          </div>
        </div>

        <div v-if="!transferUnavailable" class="flex flex-col gap-2">
          <div class="text-label text-text-muted">{{ t('task.Please_select_project_to_move') }}</div>
          <AppSearchBox
            :items="getProjectsList()"
            :getLabel="project => project.name"
            :placeholder="t('reports.search')"
            :closeOnSelect="false"
            @select="handleTransferProject"
          >
            <template #item="{ item }">
              <ProjectAvatar
                :project="item"
                class="size-6 rounded-chip border border-line"
              />
              <div class="truncate text-left">{{ item.name }}</div>
              <span
                class="ml-auto flex size-5 shrink-0 items-center justify-center rounded-full border"
                :class="selectedTransferProject?.id === item.id
                  ? 'border-accent bg-accent text-on-accent'
                  : 'border-line'"
              >
                <IconCheck16Solid v-if="selectedTransferProject?.id === item.id" class="size-3" />
              </span>
            </template>
          </AppSearchBox>

          <Callout icon="timer-manual-time">
            {{ t('task.time_save_but_screenshots_unavailable') }}
          </Callout>
        </div>

        <div v-else class="text-body text-text-muted">
          {{ t('task.No_transfer_available') }}: {{ transferUnavailable }}
        </div>

        <div class="-mx-4 border-t border-line"></div>

        <div class="flex gap-2">
          <SimpleButton
            size="md"
            class="flex-1"
            :text="t('board.CANCEL')"
            @click="closeTransferPopup"
          />
          <SimpleButton
            v-if="!transferUnavailable"
            variant="accent"
            size="md"
            class="flex-1"
            :disabled="!selectedTransferProject"
            :text="`${t('task.move_to')} ${selectedTransferProject?.name || ''}`"
            @click="onTransferTaskConfirm"
          />
        </div>
      </div>
    </SmallPopup>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { debounce } from 'vue-debounce';
import ChecklistProgress from '@/components/elements/ChecklistProgress';
import SingleLineInput from '@/components/elements/SingleLineInput';
import AppSearchBox from '@/components/elements/AppSearchBox';
import Callout from '@/components/elements/Callout';
import AppItemsList from '@/components/elements/AppItemsList';
import ProjectAvatar from '@/components/elements/ProjectAvatar';
import RuleButtons from '@/components/elements/RuleButtons';
import SpentTime from '@/components/elements/SpentTime.vue';
import { formatDuration } from '@/utils/formatDuration';
import PropertyTile from '@/components/widgets/taskRightBar/PropertyTile';
import MemberTile from '@/components/widgets/taskRightBar/MemberTile';
import PriorityTile from '@/components/widgets/taskRightBar/PriorityTile';
import TrackerChips from '@/components/widgets/taskRightBar/TrackerChips';
import SimpleButton from '../elements/SimpleButton.vue';
import SmallPopup from '../elements/SmallPopup.vue';
import {
  IconArrowDownOnSquare16Solid,
  IconCheck16Solid,
  IconCamera16Solid,
  IconClock16Solid,
  IconEllipsisVertical16Solid,
  IconHeart16Solid,
  IconPencil16Solid,
  IconShare16Solid,
  IconSquaresPlus16Solid,
} from '@iconify-prerendered/vue-heroicons';
import { IconTrash } from '@iconify-prerendered/vue-tabler';
import eventBus from '@/eventBus';


import { useMagicKeys } from '@vueuse/core';
import CloseButton from '../elements/CloseButton.vue';

// Define props
const props = defineProps({
  screenshotsPage: {
    type: Boolean,
    default: true,
  },
  transferUser: null,
});

// Define emits
const emit = defineEmits([
  'closeMobile',
  'deleteTaskDialog',
  'leaveScreenshotsPage',
  'screenshotsPage',
  'manualTimeDialog',
  'scrollToManualTime',
  'closeDialog',
]);


const keys = useMagicKeys();
watch(keys['ctrl+alt+n'], (pressed) => {
  if (pressed) {
    toggleCreateSubtask();
  }
});


// Setup store
const store = useStore();
const { t } = useI18n();

const mobileMode = computed(() => store.state.system.mobileMode);

// Reactive data
const showTransferPopup = ref(false);
const timeoutUpdatePriority = ref(null);
const nestedRef = ref(null);
const selectedTransferProject = ref(null);

// the dialog must not close on Esc while a screenshot is zoomed open
const imageIsNotZoomed = () => !document.querySelector('.medium-zoom-image--opened');


const user = computed(() => store.state.user.user);
const project = computed(() => store.state.project.project);
const taskUnderEdit = computed(() => store.state.project.taskUnderEdit);
const projectsList = computed(() => store.state.project.projectsList);

const getUser = computed(() => store.getters['user/getUser']);
const assigneeSelectList = computed(() => store.getters['task/assigneeSelectList']);
const executorSelectList = computed(() => store.getters['task/executorSelectList']);

const memberSelectItems = (buildList) => {
  if (!taskUnderEdit.value) return [];
  return buildList(taskUnderEdit.value, project.value?.projectMembers, getUser.value);
};

const assigneeSelectListItems = computed(() => memberSelectItems(assigneeSelectList.value));
const executorSelectListItems = computed(() => memberSelectItems(executorSelectList.value));


// Actions
const actionUpdateTask = (payload) => store.dispatch('project/actionUpdateTask', payload);
const actionRunRules = (payload) => store.dispatch('project/actionRunRules', payload);
const actionTransferTask = (payload) => store.dispatch('project/actionTransferTask', payload);
const actionGetTaskAndOpen = (payload) => store.dispatch('project/actionGetTaskAndOpen', payload);
const setLabelForTask = (payload) => store.dispatch('task/setLabelForTask', payload);
const taskLikedControl = (payload) => store.dispatch('task/taskLikedControl', payload);
const actionMoveTask = (payload) => store.dispatch('project/actionMoveTask', payload);

// Mutations
const updateTask = (payload) => store.commit('project/updateTask', payload);

// Computed properties
const task = computed(() => taskUnderEdit.value);

const shouldEstAutofocus = computed(() => !window.devicePlatformIsMobile);

const checklist = computed(() => (task.value.checklist ? JSON.parse(task.value.checklist) : []));

const transferUnavailable = computed(() => {
  if (![taskUnderEdit.value.creatorId].includes(user.value.id)) {
    return t('task.transfer_unavailable.not_creator');
  }
  if (getProjectsList().length === 0) {
    return t('task.transfer_unavailable.single_project');
  }
  if (taskUnderEdit.value.parentTaskId) {
    return t('task.transfer_unavailable.child_task');
  }
  return false;
});



const getRulesStage = computed(() => {
  const rules = project.value.rules?.find(x => x.stageId === taskUnderEdit.value.stageId);
  return rules?.buttons;
});

const stageName = computed(() => {
  if (project.value && project.value.stages && task.value.stageId) {
    const stage = project.value.stages.find(s => s.id === task.value.stageId);
    if (stage && stage.name) {
      return stage.name;
    }
  }
  return '';
});

const ttbus = computed(() => {
  let result = [];
  if (task.value && task.value.taskTrackedByUsers) {
    result = task.value.taskTrackedByUsers.reduce((acc, ttbu) => {
      const allTime = ttbu.totalSeconds
        + ((ttbu.timeSlots?.length && ttbu.timeSlots[0].actualTimeSeconds) || 0);

      if (
        ttbu.totalSeconds
        || (ttbu.timeSlots && ttbu.timeSlots?.length && ttbu.timeSlots[0].trackStarted)
      ) {
        acc.push({
          id: ttbu.id,
          ttbuObj: ttbu,
          allTime,
          userId: ttbu.userId,
        });
      }

      return acc;
    }, []);
  }
  const sortedResult = result && result.sort((a, b) => b.allTime - a.allTime);
  return sortedResult;
});

// estimate is set in story points, one point is one hour (same as views/popups/approveWindow.vue)
const ESTIMATE_POINT_SECONDS = 3600;

const spentSeconds = computed(() => ttbus.value.reduce((acc, ttbu) => acc + ttbu.allTime, 0));

const manualSeconds = computed(() => (task.value.manualTime || [])
  .filter(mt => mt.status === 'approved')
  .reduce((acc, mt) => acc + mt.duration, 0));

const manualPercent = computed(() => Math.floor((manualSeconds.value / spentSeconds.value) * 100));

const spentPercent = computed(() => {
  if (!task.value.estimate) {
    return null;
  }
  return Math.round((spentSeconds.value / (task.value.estimate * ESTIMATE_POINT_SECONDS)) * 100);
});

const currentUserTime = computed(() => {
  if (!user.value || !ttbus.value) {
    return { tracking: false, startPoint: 0 };
  }
  const currentUserTTBU = ttbus.value.find(ttbu => ttbu.userId === user.value.id && ttbu.ttbuObj.trackingOnline);
  return currentUserTTBU ? { tracking: true, startPoint: currentUserTTBU.allTime } : { tracking: false, startPoint: 0 };
});

const prefixName = computed(() => {
  const nameSlug = task.value?.project?.nameSlug;
  return `${nameSlug}/${task.value.hid}`;
});

// Methods
const clearEstimate = () => {
  task.value.estimate = null;
  actionUpdateTask({
    id: task.value.id,
    stageId: task.value.stageId,
    projectId: task.value.projectId,
    estimate: null,
  });
};

const updateEstimate = (est) => {
  if (+est || est === '0') {
    task.value.estimate = est;
    actionUpdateTask({
      id: task.value.id,
      stageId: task.value.stageId,
      projectId: task.value.projectId,
      estimate: +est,
    });
  }
};

const estimateDropdownRef = ref(null);

const menuActions = computed(() => [
  { label: t('task.Delete_task'), icon: IconTrash, run: () => emit('deleteTaskDialog') },
  { label: t('task.Change_project'), icon: IconArrowDownOnSquare16Solid, run: openTransferPopup },
]);

const openTransferPopup = () => {
  showTransferPopup.value = true;
};

const closeTransferPopup = () => {
  showTransferPopup.value = false;
  selectedTransferProject.value = null;
};

const updateEstimateDebounce = debounce(function(est) {
  updateEstimate(Math.floor(est));
}, 500);

function onEstimateEnter(e) {
  updateEstimate(e.target.value);
  estimateDropdownRef.value?.close();
}

const getProjectsList = () => {
  return projectsList.value.filter(p => p.id !== task.value.projectId).map(p => ({ ...p }));
};

const updatePriority = (priority) => {
  if (timeoutUpdatePriority.value) {
    clearTimeout(timeoutUpdatePriority.value);
  }
  let nextPriority;
  if (priority === 1) {
    if (task.value.priority < 1) {
      nextPriority = 1;
    } else {
      nextPriority = task.value.priority === 1 ? 2 : 1;
    }
  } else {
    nextPriority = priority;
  }
  if (task.value.priority !== nextPriority) {
    const taskUpd = {
      id: task.value.id,
      stageId: task.value.stageId,
      projectId: task.value.projectId,
      priority: nextPriority,
    };
    updateTask(taskUpd);
    timeoutUpdatePriority.value = setTimeout(() => {
      actionUpdateTask(taskUpd);
    }, 500);
  }
};

const updateAssignee = (assignee) => {
  actionUpdateTask({
    id: task.value.id,
    stageId: task.value.stageId,
    projectId: task.value.projectId,
    assigneeId: assignee ? assignee.id : null,
    ...(
      (!task.value.executorId && assignee)
        ? { executorId: assignee.id }
        : {}
    ),
  });
};


const updateExecutor = async (executor) => {
  actionUpdateTask({
    id: task.value.id,
    stageId: task.value.stageId,
    projectId: task.value.projectId,
    executorId: executor ? executor.id : null,
  });
};

const updateStage = async (stage) => {
  actionMoveTask({
    task: {
      id: task.value.id,
      projectId: task.value.projectId,
      stageId: stage.id,
    },
    stageIdFrom: task.value.stageId,
    stageIdTo: stage.id,
  });
};

const runRules = (type) => {
  const callbackCloseDialog = () => {
    emit('closeDialog');
  };

  actionRunRules({
    task: task.value,
    stageRules: getRulesStage.value,
    type,
    mentionId: props.transferUser,
    callbackCloseDialog,
  });
};

const copyTaskLink = () => {
  const dummy = document.createElement('input');
  const text = window.location.href.replace('/#/', '/');

  document.body.appendChild(dummy);
  dummy.value = text;
  dummy.select();
  dummy.setSelectionRange(0, 99999); // For mobile devices
  document.execCommand('copy');
  document.body.removeChild(dummy);

  store.commit('system/showAlert', {
    type: 'success',
    message: t('task.copied_to_clipboard'),
    timeout: 5,
  });
};



const getCurrentTtbu = () => {
  let result = {};
  if (!user.value || !task.value) {
    return result;
  }
  if (task.value && task.value.taskTrackedByUsers) {
    task.value.taskTrackedByUsers.forEach((element) => {
      if (element.userId === user.value.id) {
        result = element;
      }
    });
  }
  return result;
};

function handleRootClick(e) {
  // Only react in mobile mode
  if (!mobileMode.value) return;
  // If click target is outside the sliding panel (nested section) – close
  if (nestedRef.value && !nestedRef.value.contains(e.target)) {
    emit('closeMobile');
  }
}

function toggleCreateSubtask() {
  // Open CreateTask popup in header and pass parentTaskId after mount
  eventBus.$emit('toggle-create-subtask', task.value?.id);
}

async function onTransferTaskConfirm() {
  const payload = {
    task: { ...task.value },
    targetProject: { ...selectedTransferProject.value },
  };
  closeTransferPopup();
  const transferredTask = await actionTransferTask(payload);
  actionGetTaskAndOpen({ id: transferredTask.id });
}

function handleTransferProject(project) {
  selectedTransferProject.value = project;
}
</script>
